import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [agency, setAgency] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userType, setUserType] = useState(localStorage.getItem('userType'));
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      if (userType === 'agency') {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgency(response.data);
      } else if (userType === 'customer') {
        const response = await axios.get(`${API_URL}/api/customer/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomer(response.data);
      } else if (userType === 'admin') {
        setAdmin({ email: localStorage.getItem('adminEmail') });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, userType]);

  useEffect(() => {
    if (token && userType) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [token, userType, fetchProfile]);

  const loginAgency = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, agency: agencyData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userType', 'agency');
    setToken(newToken);
    setUserType('agency');
    setAgency(agencyData);
    return agencyData;
  };

  const registerAgency = async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    const { token: newToken, agency: agencyData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userType', 'agency');
    setToken(newToken);
    setUserType('agency');
    setAgency(agencyData);
    return agencyData;
  };

  const loginCustomer = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/customer/login`, { email, password });
    const { token: newToken, customer: customerData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userType', 'customer');
    setToken(newToken);
    setUserType('customer');
    setCustomer(customerData);
    return customerData;
  };

  const registerCustomer = async (data) => {
    const response = await axios.post(`${API_URL}/api/customer/register`, data);
    const { token: newToken, customer: customerData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userType', 'customer');
    setToken(newToken);
    setUserType('customer');
    setCustomer(customerData);
    return customerData;
  };

  const loginAdmin = async (email, password, masterKey) => {
    const response = await axios.post(`${API_URL}/api/admin/login`, { email, password, master_key: masterKey });
    const { token: newToken } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userType', 'admin');
    localStorage.setItem('adminEmail', email);
    setToken(newToken);
    setUserType('admin');
    setAdmin({ email });
    return { email };
  };

  const registerAdmin = async (email, password, masterKey) => {
    const response = await axios.post(`${API_URL}/api/admin/register`, { email, password, master_key: masterKey });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('adminEmail');
    setToken(null);
    setUserType(null);
    setAgency(null);
    setCustomer(null);
    setAdmin(null);
  };

  const updateAgencyProfile = async (data) => {
    const response = await axios.put(`${API_URL}/api/auth/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAgency(response.data);
    return response.data;
  };

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{
      agency,
      customer,
      admin,
      token,
      userType,
      loading,
      loginAgency,
      registerAgency,
      loginCustomer,
      registerCustomer,
      registerCustomer,
      loginAdmin,
      registerAdmin,
      logout,
      updateAgencyProfile,
      getAuthHeader,
      loading, // Add loading here
      isAuthenticated: !!token,
      isAgency: userType === 'agency' && !!agency,
      isCustomer: userType === 'customer' && !!customer,
      isAdmin: userType === 'admin' && !!admin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
